<template>
  <div class="border-top p-2 d-flex align-items-end rs-bg-content">
    <!-- Column Manager -->
    <BDropdown size="sm" text="Columns" variant="light" class="mr-2">
      <BDropdownItemButton @click="editor.chain().focus(position).addColumnBefore().run()">
        <i class="far fa-plus mr-2" />Add Column Left
      </BDropdownItemButton>
      <BDropdownItemButton @click="editor.chain().focus(position).addColumnAfter().run()">
        <i class="far fa-plus mr-2" />Add Column Right
      </BDropdownItemButton>

      <BDropdownDivider />

      <BDropdownItemButton @click="editor.chain().focus(position).toggleHeaderColumn().run()">
        <i class="fad fa-grip-lines-vertical mr-2" />Toggle Column Headers
      </BDropdownItemButton>

      <BDropdownDivider />

      <BDropdownItemButton @click="editor.chain().focus(position).deleteColumn().run()">
        <i class="far fa-trash-alt mr-2 rs-text-error" />Remove Column
      </BDropdownItemButton>
    </BDropdown>

    <!-- Row Manager -->
    <BDropdown size="sm" text="Rows" variant="light" class="mr-2">
      <BDropdownItemButton @click="editor.chain().focus(position).addRowBefore().run()">
        <i class="far fa-plus mr-2" />Add Row Above
      </BDropdownItemButton>
      <BDropdownItemButton @click="editor.chain().focus(position).addRowAfter().run()">
        <i class="far fa-plus mr-2" />Add Row Below
      </BDropdownItemButton>

      <BDropdownDivider />

      <BDropdownItemButton @click="editor.chain().focus(position).toggleHeaderRow().run()">
        <i class="fad fa-grip-lines mr-2" />Toggle Row Headers
      </BDropdownItemButton>

      <BDropdownDivider />

      <BDropdownItemButton @click="editor.chain().focus(position).deleteRow().run()">
        <i class="far fa-trash-alt mr-2 rs-text-error" />Remove Row
      </BDropdownItemButton>
    </BDropdown>

    <!-- Table Proeprties Manager -->
    <BDropdown size="sm" text="Table" variant="light" class="">
      <BDropdownForm :style="{ width: '300px' }">
        <BInputGroup class="mb-3" size="sm" prepend="Width">
          <BFormInput v-model="width" type="number" @input="handleWidthChange" />

          <BInputGroupAppend>
            <BButton
              title="Table width in pixels"
              :variant="tableWidthUoM === 'px' ? 'secondary' : 'outline-secondary'"
              @click="handleWidthUoMChange('px')"
            >
              px
            </BButton>
            <BButton
              title="Table width in percentage"
              :variant="tableWidthUoM === '%' ? 'secondary' : 'outline-secondary'"
              @click="handleWidthUoMChange('%')"
            >
              &percnt;
            </BButton>
          </BInputGroupAppend>
        </BInputGroup>

        <BInputGroup class="mb-3" prepend="Border" append="px" size="sm">
          <BFormInput
            v-model="border"
            type="number"
            @input="handleBorderChange"
          />
        </BInputGroup>

        <BInputGroup class="mb-3" append="px" prepend="Cell Padding" size="sm">
          <BFormInput
            v-model="cellPadding"
            type="number"
            @input="handleCellPaddingChange"
          />
        </BInputGroup>

        <BInputGroup class="mb-3" prepend="Cell Spacing" append="px" size="sm">
          <BFormInput
            v-model="cellSpacing"
            type="number"
            @input="handleCellSpacingChange"
          />
        </BInputGroup>

        <BInputGroup prepend="Align" size="sm">
          <BFormSelect
            v-model="align"
            :options="tblAlignOptions"
            @change="handleAlignChange"
          />
        </BInputGroup>
      </BDropdownForm>

      <BDropdownDivider />

      <BDropdownItemButton @click="handleDelete">
        <i class="far fa-trash-alt mr-2 rs-text-error" />Remove Table
      </BDropdownItemButton>
    </BDropdown>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { BDropdownDivider, BFormSelect } from 'bootstrap-vue';
import { Editor } from '@tiptap/vue-2';
import { tblAlignOptions } from '@/shared/tiptap/consts';
import { findNodePosByElement, setNodePosAttributes } from '@/shared/tiptap/helpers';
import { TEditableTable } from '@/shared/tiptap/types';

export default defineComponent({
  props: {
    editor: {
      type: Object as PropType<Editor>,
      required: true,
    },

    onTableRemove: {
      type: Function as PropType<() => void>,
      default: () => {},
    },
  },

  data() {
    return {
      tblAlignOptions,
      position: 0,
      tableWidthUoM: '%',
      border: 1,
      width: 100,
      cellSpacing: 1,
      cellPadding: 1,
      align: '',
    };
  },

  watch: {
    'editor.state.selection': {
      immediate: true,
      handler(newVal: { from: number, to: number }) {
        if (this.editor.isFocused && this.editor.isActive('table')) {
          this.position = newVal.from;
        }
      },
    },
  },

  mounted() {
    // Timeout workaround that ensures Vue picks up UI elements in the mounted hook.
    // The same workaround is often used for $refs.
    // Vue claims to have refs / ui available in mounted, but it's bulls**t
    setTimeout(() => {
      this.setInitialState();
    }, 100);
  },

  methods: {
    /**
     * Under the hood, TipTap's NodePos reperesentation of a table is the `tbody` tag,
     * but attributes get set at the table element level. So in this case we need to always
     * return the table element to make sure we are processing and updating its attributes.
     */
    getTableNodePos() {
      const tBodyNode = findNodePosByElement<HTMLTableElement>(this.editor, this.position, 'table');

      return {
        nodePos: tBodyNode.nodePos,
        element: (
          tBodyNode.element?.tagName.toUpperCase() === 'TBODY'
            ? tBodyNode.element?.closest('table')
            : tBodyNode.element
        ),
      };
    },

    /**
     * Sets width and UoM editable state based on values in the following order:
     * 1. Width in `style` attribute
     * 2. Width in `width` attribute
     * 3. Falls back to `100` width with `%` UoM
     */
    setEditableWidth() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const width = element.width || element.style.width;

      if (!width) {
        this.width = 100;
        this.tableWidthUoM = '%';
        return;
      }

      if (['%', 'px'].some((uom) => element.style.width.endsWith(uom))) {
        this.width = Number(width.replace(/%|px/, ''));
        this.tableWidthUoM = width.includes('%') ? '%' : 'px';
        return;
      }

      this.width = Number(width);
      this.tableWidthUoM = 'px';
    },

    /**
     * Sets the border editable state based on values in the following order:
     * 1. `border-width` in `style` attribute.
     * 2. `border` attribute
     * 3. Falls back to `1`.
     */
    setEditableBorder() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const borderWidth = element.style.borderWidth || element.border;

      if (!borderWidth) {
        this.border = 1;
        return;
      }

      this.border = Number(borderWidth.replace(/%|px/, ''));
    },

    /**
     * Sets the cell spacing editable state based on values in the following order:
     * 1. `border-spacing` in `style`.
     * 2. `cellspacing` attribute
     * 3. Falls back to `1`.
     */
    setEditableCellSpacing() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const cellSpacing = element.style.borderSpacing || element.cellSpacing;

      if (!cellSpacing) {
        this.cellSpacing = 1;
        return;
      }

      this.cellSpacing = Number(cellSpacing.replace(/%|px/, ''));
    },

    /**
     * Sets the cell padding editable state based on values in the following order:
     * 1. `padding` in `style` attribute of the first `<td>` child found in table element.
     * 2. `cellpadding` attribute
     * 3. Falls back to `1`.
     */
    setEditableCellPadding() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const cell = element.querySelector('td');
      const cellPadding = cell?.style.padding || element.cellPadding;

      if (!cellPadding) {
        this.cellPadding = 1;
        return;
      }

      this.cellPadding = Number(cellPadding.replace(/%|px/, ''));
    },

    /**
     * Set initial alignment state on table.
     * 1. Checks for `float` or `margin` on the table style attributes.
     * 2. If not in style, checks the align attribute.
     */
    setEditableAlignment() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      if (element.style.margin.includes('auto')) {
        this.align = 'center';
        return;
      }

      this.align = element.style.float ?? '';
    },

    /**
     * Runs all state setting / resetting methods
     */
    setInitialState() {
      this.width = 100;
      this.tableWidthUoM = '%';
      this.border = 1;
      this.cellSpacing = 1;
      this.cellPadding = 1;
      this.align = '';

      this.setEditableWidth();
      this.setEditableBorder();
      this.setEditableCellPadding();
      this.setEditableCellSpacing();
      this.setEditableAlignment();
    },

    /**
     * Changes value of a key in the `TEditable` state object. Used by other methods
     * after manipulating style values and DOM to sync with the editor's attributes.
     * @param key
     * @param content
     */
    handleChange(newAttrs: Partial<Record<keyof TEditableTable, string | null>>) {
      const { nodePos } = this.getTableNodePos();

      if (!nodePos) {
        return;
      }

      const newValue = { ...nodePos.attributes, ...newAttrs };
      setNodePosAttributes(nodePos, newValue);

      // Small hack to trigger an update. Adds space to beginning then undoes it
      this.editor.commands.insertContentAt(0, ' ', { updateSelection: false });
      this.editor.commands.undo();
    },

    /**
     * Handles the change of width, including accounting or width unit of measurement.
     * Syncs formatted width with the passed `value` prop.
     * @param newVal
     */
    handleWidthChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const formattedWidth = `${newVal}${this.tableWidthUoM}`;
      element.style.width = formattedWidth;

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        width: formattedWidth,
      });
    },

    /**
     * Handles the change of width's unit of measurement ('px' or '%')
     * Syncs formatted width with the passed `value` prop.
     * @param newVal
     */
    handleWidthUoMChange(newVal: 'px' | '%') {
      this.tableWidthUoM = newVal;

      // If width is greater than 100px currently, set it to 100%.
      if (newVal === '%' && (this.width ?? 100) > 100) {
        this.handleWidthChange('100');
        return;
      }

      this.handleWidthChange(`${this.width}`);
    },

    /**
     * Handles the change of border width.
     * Syncs formatted border width with the passed `value` prop.
     * @param newVal
     */
    handleBorderChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const formattedWidth = `${(newVal || '0')}px`;
      element.style.borderWidth = formattedWidth;

      if (!element.style.borderStyle) {
        element.style.borderStyle = 'solid';
      }

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        border: newVal || '0',
      });
    },

    /**
     * Handles the change of border spacing / cell spacing.
     * Syncs formatted border spacing with the passed `value` prop.
     * @param newVal
     */
    handleCellSpacingChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const formattedWidth = `${(newVal || '0')}px`;
      element.style.borderSpacing = formattedWidth;

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        cellspacing: newVal || '0',
      });
    },

    /**
     * Handles the change of cell padding.
     * Syncs formatted border spacing with the passed `value` prop.
     * @param newVal
     */
    handleCellPaddingChange(newVal: string) {
      const { nodePos, element } = this.getTableNodePos();

      if (!element || !nodePos) {
        return;
      }

      const formattedWidth = `${(newVal || '0')}px`;
      const tds = nodePos.querySelectorAll('tableCell');
      const ths = nodePos.querySelectorAll('tableHeader');

      [...tds, ...ths ].forEach((td) => {
        td.element.style.padding = formattedWidth;
        td.attributes.style = td.element.getAttribute('style') ?? '';
      });

      this.handleChange({ cellpadding: newVal || '0' });
    },

    /**
     * Removes the table node found at position (the current table being edited)
     */
    handleDelete() {
      this.editor.chain().focus(this.position).deleteTable().run();

      if (this.onTableRemove) {
        this.onTableRemove();
      }
    },

    /**
     * Handles alignment of the table against other content.
     * Uses margin auto for center alignment or float left / right for other alignments.
     * @param newVal
     */
    handleAlignChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      element.style.margin = '';
      element.style.float = '';

      if (newVal === 'center') {
        element.style.margin = '0 auto';
      }

      if (['left', 'right'].includes(newVal)) {
        element.style.float = newVal;
      }

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        align: newVal || null,
      });
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
</style>
